import { ITypedReduxAction } from '../../types/redux';
import { IShareCollection, IShareCollectionState, ShareType } from '../../types/shareCollection';
import { actionGenerator } from '../../utils/redux';

export enum EShareModalActionTypes {
  Set = 'SHARE_MODAL :: SET',
  Open = 'SHARE_MODAL :: OPEN',
  Close = 'SHARE_MODAL :: CLOSE',
  SetName = 'SHARE_MODAL :: SET_NAME',
  SetHideNotes = 'SHARE_MODAL :: SET_HIDE_NOTES',
  SetHideContacts = 'SHARE_MODAL :: SET_HIDE_CONTACTS',
  SetHideAddresses = 'SHARE_MODAL :: SET_HIDE_ADDRESSES',
  SetHideOffersLiks = 'SHARE_MODAL :: SET_HIDE_OFFERS_LINL',
  SetShareType = 'SHARE_MODAL :: SET_SHARE_TYPE',
  SetWhatsAppText = 'SHARE_MODAL :: SET_WHATS_APP_TEXT',
  SetUrl = 'SHARE_MODAL :: SET_URL',
  SetUpdatedAt = 'SHARE_MODAL :: UPDATED_AT',
  SetIsFetching = 'SHARE_MODAL :: IS_FETCHING',
  SetShareTooltip = 'SHARE_MODAL :: SHOW_TOOLTIP',
  SetShareSettings = 'SHARE_MODAL :: SET_SETTINGS',
}

export interface ISharedCollectionIdPayload {
  id: number;
}

export interface ISharedCollectionSet extends ISharedCollectionIdPayload {
  name: string;
  hideNotes: boolean;
  hideContacts: boolean;
  hideAddresses: boolean;
  hideOffersLink: boolean;
  url: string | null;
}

export interface ISharedCollectionOpen extends ISharedCollectionIdPayload {
  name: string;
}

export interface ISetSharedCollectionNamePayload extends ISharedCollectionIdPayload {
  value: string;
}

export interface IShareModalFlagPayload extends ISharedCollectionIdPayload {
  value: boolean;
}

export interface ISetShareModalTypePayload extends ISharedCollectionIdPayload {
  value: ShareType;
}

export interface ISetShareIsFetchingTypePayload extends ISharedCollectionIdPayload {
  isFetchingType: IShareCollection['isFetching'];
}

export type TSetShareTooltipPayload = IShareCollectionState['tooltip'];

export type TShareModalAction =
  | ITypedReduxAction<EShareModalActionTypes.Set, ISharedCollectionSet>
  | ITypedReduxAction<EShareModalActionTypes.Open, ISharedCollectionOpen>
  | ITypedReduxAction<EShareModalActionTypes.Close, void>
  | ITypedReduxAction<EShareModalActionTypes.SetName, ISetSharedCollectionNamePayload>
  | ITypedReduxAction<EShareModalActionTypes.SetHideNotes, IShareModalFlagPayload>
  | ITypedReduxAction<EShareModalActionTypes.SetHideContacts, IShareModalFlagPayload>
  | ITypedReduxAction<EShareModalActionTypes.SetHideOffersLiks, IShareModalFlagPayload>
  | ITypedReduxAction<EShareModalActionTypes.SetHideAddresses, IShareModalFlagPayload>
  | ITypedReduxAction<EShareModalActionTypes.SetShareType, ISetShareModalTypePayload>
  | ITypedReduxAction<EShareModalActionTypes.SetWhatsAppText, ISetSharedCollectionNamePayload>
  | ITypedReduxAction<EShareModalActionTypes.SetUrl, ISetSharedCollectionNamePayload>
  | ITypedReduxAction<EShareModalActionTypes.SetIsFetching, ISetShareIsFetchingTypePayload>
  | ITypedReduxAction<EShareModalActionTypes.SetUpdatedAt, ISetSharedCollectionNamePayload>
  | ITypedReduxAction<EShareModalActionTypes.SetShareTooltip, TSetShareTooltipPayload>
  | ITypedReduxAction<EShareModalActionTypes.SetShareSettings, ISharedCollectionSet>;

export const setShareModal: (payload: ISharedCollectionSet) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.Set,
  ISharedCollectionSet
>(EShareModalActionTypes.Set);

export const setShareSettings: (payload: ISharedCollectionSet) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetShareSettings,
  ISharedCollectionSet
>(EShareModalActionTypes.SetShareSettings);

export const openShareModal: (payload: ISharedCollectionOpen) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.Open,
  ISharedCollectionOpen
>(EShareModalActionTypes.Open);

export const closeShareModal: () => TShareModalAction = actionGenerator<EShareModalActionTypes.Close>(
  EShareModalActionTypes.Close,
);

export const setSharedCollectionName: (payload: ISetSharedCollectionNamePayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetName,
  ISetSharedCollectionNamePayload
>(EShareModalActionTypes.SetName);

export const setShareModalHideNotes: (payload: IShareModalFlagPayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetHideNotes,
  IShareModalFlagPayload
>(EShareModalActionTypes.SetHideNotes);

export const setShareModalHideContacts: (payload: IShareModalFlagPayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetHideContacts,
  IShareModalFlagPayload
>(EShareModalActionTypes.SetHideContacts);

export const setShareModalHideOffersLink: (payload: IShareModalFlagPayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetHideOffersLiks,
  IShareModalFlagPayload
>(EShareModalActionTypes.SetHideOffersLiks);

export const setShareModalHideAddresses: (payload: IShareModalFlagPayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetHideAddresses,
  IShareModalFlagPayload
>(EShareModalActionTypes.SetHideAddresses);

export const setShareModalType: (payload: ISetShareModalTypePayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetShareType,
  ISetShareModalTypePayload
>(EShareModalActionTypes.SetShareType);

export const setShareModalWhatsAppText: (payload: ISetSharedCollectionNamePayload) => TShareModalAction =
  actionGenerator<EShareModalActionTypes.SetWhatsAppText, ISetSharedCollectionNamePayload>(
    EShareModalActionTypes.SetWhatsAppText,
  );

export const setShareModalUrl: (payload: ISetSharedCollectionNamePayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetUrl,
  ISetSharedCollectionNamePayload
>(EShareModalActionTypes.SetUrl);

export const setShareModalUpdatedAt: (payload: ISetSharedCollectionNamePayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetUpdatedAt,
  ISetSharedCollectionNamePayload
>(EShareModalActionTypes.SetUpdatedAt);

export const setShareModalIsFetching: (payload: ISetShareIsFetchingTypePayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetIsFetching,
  ISetShareIsFetchingTypePayload
>(EShareModalActionTypes.SetIsFetching);

export const setTooltip: (payload: TSetShareTooltipPayload) => TShareModalAction = actionGenerator<
  EShareModalActionTypes.SetShareTooltip,
  TSetShareTooltipPayload
>(EShareModalActionTypes.SetShareTooltip);
